import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Card, Dialog, Menu, Skeleton, StickyBottomActions } from "../../../design-v2";
import { createExecutionWorkoutPreferencesLocation } from "../../settings/settingsNavigation";
import ExecutionItemCard from "../components/ExecutionItemCard";
import ExercisePickerSheet from "../components/ExercisePickerSheet";
import FirstUsePreferenceSheet from "../components/FirstUsePreferenceSheet";
import RestTimerBar from "../components/RestTimerBar";
import ReviewExerciseList from "../components/ReviewExerciseList";
import ReviewReflection from "../components/ReviewReflection";
import SupersetExecutionGroup from "../components/SupersetExecutionGroup";
import SyncAuthNotice from "../components/SyncAuthNotice";
import SyncIssueNotice from "../components/SyncIssueNotice";
import SyncStatusChip from "../components/SyncStatusChip";
import { createClientUuid } from "../domain/clientIds";
import { EXECUTION_ACTIONS } from "../domain/executionReducer";
import { nextExecutionItemPosition } from "../domain/executionLimits";
import {
  executionClassification,
  executionCounts,
  executionSetProgress,
  formatReviewDuration,
  formatTrackedDuration,
  needsOlympicConvention,
  needsWeightUnit,
  trackedDurationSeconds,
} from "../domain/executionUi";
import {
  effectiveExecutionProfile,
  prescribedExerciseForItem,
} from "../domain/effectiveExecutionProfile";
import {
  SUBSTITUTION_ERRORS,
  substitutionErrorCopy,
  substitutionPresentation,
} from "../domain/substitution";
import { recordSyncAction, recordSyncIssue, syncConflictCopy } from "../domain/syncIssue";
import {
  previousPerformanceEnabled,
  WORKOUT_PREFERENCE_DEFAULTS,
} from "../domain/workoutPreferenceDefaults";
import useCompactIdentityOnScroll, {
  IDENTITY_PHASE,
  useCompactRowLayout,
} from "../hooks/useCompactIdentityOnScroll";
import useConnectivity from "../hooks/useConnectivity";
import useExecution from "../hooks/useExecution";
import useLiveExecutionDuration from "../hooks/useLiveExecutionDuration";
import useRestTimer, { REST_DISPLAY } from "../hooks/useRestTimer";
import useScreenWakeLock from "../hooks/useScreenWakeLock";
import useExecutionOwnership, {
  EXECUTION_OPEN_ELSEWHERE_MESSAGE,
} from "../hooks/useExecutionOwnership";
import useSyncStatus from "../hooks/useSyncStatus";
import useWorkoutPreferences from "../hooks/useWorkoutPreferences";
import useTrainIdentity, { canUseServer, needsSignInToSync } from "../recovery/trainIdentity";
import { MOTION, captureRects, motionAllowed, playFrom, settleChromeEdge } from "../motion";

function HeaderIcon({ name }) {
  const paths = {
    back: <path d="M15 18l-6-6 6-6M9 12h11" />,
    settings: <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm7.4-3.5a7.7 7.7 0 0 0-.08-1.06l2.03-1.58-2-3.46-2.48 1a8 8 0 0 0-1.83-1.06L14.68 3h-4l-.36 2.84A8 8 0 0 0 8.5 6.9L6 5.9l-2 3.46 2.04 1.58A7.7 7.7 0 0 0 5.96 12c0 .36.03.72.08 1.06L4 14.64l2 3.46 2.5-1a8 8 0 0 0 1.82 1.06l.36 2.84h4l.36-2.84a8 8 0 0 0 1.83-1.06l2.48 1 2-3.46-2.03-1.58c.05-.34.08-.7.08-1.06Z" />,
    add: <path d="M12 5v14M5 12h14" />,
    discard: <path d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6" />,
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function ActiveExecutionHeader({
  record,
  online,
  chipOnline = online,
  signInRequired = false,
  ownership,
  sync,
  disabled,
  itemLimitReached,
  saving,
  finishing,
  finishLocked = false,
  onBack,
  onFinish,
  onOpenSettings,
  onAddExercise,
  onDiscard,
  previousPerformance = true,
  onTogglePreviousPerformance,
}) {
  const progress = executionSetProgress(record);
  const hasSetProgress = progress.total > 0;
  const progressPercent = hasSetProgress
    ? Math.min(100, (progress.performed / progress.total) * 100)
    : 0;
  const elapsed = formatTrackedDuration(
    useLiveExecutionDuration(record, ownership.isOwner)
  );
  const addDisabled = disabled || !online || itemLimitReached;
  const settingsDisabled = !online || saving;
  const identityRef = useRef(null);
  const chromeRef = useRef(null);
  // Where the identity was just before it compacts or expands. The layout
  // itself still changes in one step (the scroll thresholds rely on that);
  // the pieces then glide from their old place to the new one.
  const identityBeforeRef = useRef(null);
  const captureIdentity = useCallback(() => {
    const chrome = chromeRef.current;
    const identity = identityRef.current;
    if (!chrome || !identity || !motionAllowed()) {
      identityBeforeRef.current = null;
      return;
    }
    identityBeforeRef.current = {
      height: chrome.getBoundingClientRect().height,
      rects: captureRects([...identity.children]),
    };
  }, []);
  const identityPhase = useCompactIdentityOnScroll({ identityRef, onBeforeChange: captureIdentity });
  const compact = identityPhase === IDENTITY_PHASE.COMPACT;
  const compactRow = useCompactRowLayout(identityRef, compact);
  useLayoutEffect(() => {
    const before = identityBeforeRef.current;
    identityBeforeRef.current = null;
    const chrome = chromeRef.current;
    if (!before || !chrome) return;
    playFrom(before.rects);
    settleChromeEdge(chrome, before.height - chrome.getBoundingClientRect().height);
  }, [identityPhase, compactRow]);

  return (
    <header
      ref={chromeRef}
      className="lz-workout-execution-chrome"
      data-identity={identityPhase}
      data-compact-row={compact ? compactRow : undefined}
    >
      <div
        className="lz-workout-execution-chrome__progress"
        data-empty={!hasSetProgress || undefined}
        role={hasSetProgress ? "progressbar" : undefined}
        aria-label={hasSetProgress ? "Set progress" : undefined}
        aria-valuemin={hasSetProgress ? 0 : undefined}
        aria-valuemax={hasSetProgress ? progress.total : undefined}
        aria-valuenow={hasSetProgress ? progress.performed : undefined}
      >
        <span style={{ width: `${progressPercent}%` }} />
      </div>

      <div className="lz-workout-execution-chrome__controls">
        <button
          type="button"
          className="lz-workout-execution-chrome__icon-button lz-workout-execution-chrome__back"
          aria-label="Back to Home"
          onClick={onBack}
        >
          <HeaderIcon name="back" />
        </button>

        {/* One quiet metrics capsule: live set progress | tracked duration. */}
        <div className="lz-workout-execution-chrome__metrics">
          <span
            className="lz-workout-execution-chrome__metric"
            aria-label={hasSetProgress
              ? `${progress.performed} of ${progress.total} sets performed`
              : "No set progress"}
          >
            {hasSetProgress ? (
              <><strong>{progress.performed}/{progress.total}</strong><small>sets</small></>
            ) : (
              <strong>No sets</strong>
            )}
          </span>
          <span className="lz-workout-execution-chrome__metrics-divider" aria-hidden="true" />
          <span
            className="lz-workout-execution-chrome__metric lz-workout-execution-chrome__elapsed"
            aria-label={`Active workout time ${elapsed}`}
          >
            {elapsed}
          </span>
        </div>

        <Button
          size="sm"
          className="lz-workout-execution-chrome__finish"
          aria-label="Finish & Review"
          disabled={disabled || finishing || finishLocked}
          onClick={onFinish}
        >
          Finish <span aria-hidden="true">→</span>
        </Button>

        <Menu
          exitDuration={motionAllowed() ? MOTION.exit - 40 : 0}
          className="lz-workout-execution-menu"
          menuClassName="lz-workout-execution-menu__surface"
          trigger={(
            <button
              type="button"
              className="lz-workout-execution-chrome__icon-button lz-workout-execution-chrome__more"
              aria-label="Workout menu"
            >
              <span aria-hidden="true">⋮</span>
            </button>
          )}
        >
          <button
            type="button"
            role="menuitem"
            disabled={settingsDisabled}
            aria-disabled={settingsDisabled || undefined}
            title={settingsDisabled ? "Needs a connection" : undefined}
            onClick={onOpenSettings}
          >
            <span className="lz-workout-execution-menu__icon"><HeaderIcon name="settings" /></span>
            <span className="lz-workout-execution-menu__copy">Workout Settings</span>
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={addDisabled}
            aria-disabled={addDisabled || undefined}
            onClick={onAddExercise}
          >
            <span className="lz-workout-execution-menu__icon"><HeaderIcon name="add" /></span>
            <span className="lz-workout-execution-menu__copy">
              <span>Add Exercise</span>
              {!online ? <small>Needs a connection</small> : null}
            </span>
          </button>
          {/* One global setting (§13.10). A menuitemcheckbox keeps the menu
              open, so the athlete sees the switch change. */}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={previousPerformance}
            className="lz-workout-execution-menu__switch-row"
            onClick={onTogglePreviousPerformance}
          >
            <span className="lz-workout-execution-menu__copy">Previous performance</span>
            <span className="lz-workout-execution-menu__switch" aria-hidden="true">
              <span className="lz-workout-execution-menu__switch-thumb" />
            </span>
          </button>
          <div className="lz-workout-execution-menu__separator" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="lz-workout-execution-menu__danger"
            disabled={disabled}
            aria-disabled={disabled || undefined}
            onClick={onDiscard}
          >
            <span className="lz-workout-execution-menu__icon"><HeaderIcon name="discard" /></span>
            <span className="lz-workout-execution-menu__copy">Discard Workout</span>
          </button>
        </Menu>
      </div>

      {/* Expanded at the top; one line (title · classification … chip) after
          a modest scroll. Same elements in both layouts, presentation only. */}
      <div ref={identityRef} className="lz-workout-execution-chrome__identity">
        <h1>{record.server.title}</h1>
        <p className="lz-workout-execution-chrome__classification">
          {executionClassification(record)}
        </p>
        <SyncStatusChip
          online={chipOnline}
          signInRequired={signInRequired}
          readOnly={ownership.readOnly}
          record={record}
          status={sync}
        />
      </div>
    </header>
  );
}

// FINISHED chrome: calm and static. No set-progress pill, no ticking clock and
// no Finish action; the duration shown below is the frozen tracked duration.
// The back arrow is the one way to change the workout: it returns to the
// workout screen, where the athlete edits and then finishes again.
function ReviewHeader({ record, online, signInRequired = false, ownership, sync, onBack }) {
  return (
    <>
      <header className="lz-workout-review-chrome">
        <button
          type="button"
          className="lz-workout-execution-chrome__icon-button"
          aria-label="Back to workout"
          onClick={onBack}
        >
          <HeaderIcon name="back" />
        </button>
        <span className="lz-workout-review-chrome__state">
          <span aria-hidden="true" />
          Finished
        </span>
        <SyncStatusChip
          online={online}
          signInRequired={signInRequired}
          readOnly={ownership.readOnly}
          record={record}
          status={sync}
        />
      </header>
      <div className="lz-workout-review-identity">
        <h1>Review Workout</h1>
        <p>
          <strong>{record.server.title}</strong>
          <span aria-hidden="true">·</span>
          <span>{executionClassification(record)}</span>
        </p>
      </div>
    </>
  );
}

function ReviewSummary({ counts, durationSeconds }) {
  const duration = formatReviewDuration(durationSeconds);
  return (
    <dl className="lz-workout-review-summary" aria-label="Workout summary">
      <div>
        <dt>Duration</dt>
        <dd><strong>{duration.value}</strong> <span>{duration.unit}</span></dd>
      </div>
      <div>
        <dt>Completed</dt>
        <dd className="is-performed">
          <strong>{counts.performedSets}</strong> <span>{counts.performedSets === 1 ? "set" : "sets"}</span>
        </dd>
        {counts.completedCardio > 0 ? (
          <dd className="lz-workout-review-summary__extra">+ {counts.completedCardio} cardio</dd>
        ) : null}
      </div>
      <div>
        <dt>Not performed</dt>
        <dd className="is-remaining">
          <strong>{counts.prescribedNotPerformed}</strong> <span>{counts.prescribedNotPerformed === 1 ? "set" : "sets"}</span>
        </dd>
      </div>
    </dl>
  );
}

function hasUnacknowledgedData(record) {
  return Boolean(record?.pending ||
    Number(record?.localRevision || 0) > Number(record?.ackedRevision || 0));
}

// The "Sign in to sync" action (§11.4) is offered wherever that state stands:
// a Session known to be gone (the anonymous local shell), or a Workout 401 on
// this record's unsynced data while the bootstrap still names its user (§11.3,
// I-26, H8-1). The action re-checks the Session before any navigation, so a
// stale "authenticated" bootstrap can never bounce /login back.
function offersSignInToSync(record, { signInRequired, sessionHeld }) {
  if (signInRequired) return true;
  return sessionHeld && recordSyncAction(record) === "SIGN_IN" && hasUnacknowledgedData(record);
}

function savedResultTitle(record, signInRequired = false) {
  const outcome = record.result?.outcome;
  if (outcome === "PLANNED") {
    const week = record.result?.satisfiedOccurrence?.weekNumber ??
      record.server?.intendedOccurrence?.weekNumber;
    return week != null ? `Saved · Counts toward Week ${week}` : "Saved · Counts toward your program";
  }
  if (outcome === "EXTRA") return "Saved as an Extra Workout";
  // "will sync" only while syncing is still expected to succeed on its own.
  if (recordSyncIssue(record)) return "Saved on this device · sync issue";
  const action = recordSyncAction(record);
  if (action === "SIGN_IN") return "Saved on this device · sign in to sync";
  if (action === "UPDATE") return "Saved on this device · update required";
  const unacknowledged = record.pending ||
    Number(record.localRevision || 0) > Number(record.ackedRevision || 0);
  // Signed out: waiting will not sync it, so it is never promised.
  if (unacknowledged && signInRequired) return "Saved on this device · sign in to sync";
  return unacknowledged ? "Saved on this device · will sync" : "Workout saved";
}

function SavedResult({ record, signInRequired = false, sessionHeld = false, onHome }) {
  const outcome = record.result?.outcome;
  const preStartOutcome = record.server?.projection?.outcome;
  return (
    <section className="lz-workout-result">
      <Card>
        <p className="lz-workout-eyebrow">WORKOUT SAVED</p>
        <h1>{savedResultTitle(record, signInRequired)}</h1>
        <SyncIssueNotice record={record} />
        <SyncAuthNotice
          userId={record.userId}
          visible={hasUnacknowledgedData(record) &&
            offersSignInToSync(record, { signInRequired, sessionHeld })}
        />
        {outcome && preStartOutcome && outcome !== preStartOutcome ? (
          <p className="lz-workout-muted">
            The cycle changed while you trained, so the final classification was updated.
          </p>
        ) : null}
        <Button onClick={onHome}>Back to Home</Button>
      </Card>
    </section>
  );
}

function requiredPreference(record, preferences, extraProfile = null) {
  const profiles = (record?.document?.items || [])
    .map((item) => effectiveExecutionProfile(record, item))
    .filter(Boolean);
  if (extraProfile) profiles.push(extraProfile);
  if (!preferences?.weightUnit && profiles.some(needsWeightUnit)) return "weightUnit";
  if (!preferences?.olympicBarbellConvention && profiles.some(needsOlympicConvention)) {
    return "olympicBarbellConvention";
  }
  return null;
}

function addedItem(exercise, record) {
  const itemId = createClientUuid();
  const position = nextExecutionItemPosition(record);
  if (position == null) throw new Error("This workout already has the maximum of 60 exercises.");
  const profile = exercise.executionProfile;
  const set = profile.measurementType === "CARDIO_MINUTES" ? [] : [{
    setId: createClientUuid(),
    setIndex: 1,
    origin: "ADDED",
    prescribedSetKey: null,
    setType: "WORKING",
    load: null,
    reps: null,
    seconds: null,
    rir: null,
    performedAt: null,
    lastEditedAt: new Date().toISOString(),
    enteredAfterFinish: record.document.status === "FINISHED",
  }];
  return {
    itemId,
    position,
    blockKey: `added:${itemId}`,
    laneIndex: null,
    origin: "ADDED",
    prescribedExerciseKey: null,
    substitutedFromExerciseId: null,
    exerciseId: exercise.exerciseId,
    exerciseName: exercise.name,
    measurementType: profile.measurementType,
    loadMode: profile.loadMode,
    suggestedFamily: profile.suggestedFamily || null,
    executionProfile: profile,
    // The exercise note carried from saved history, when the picker sent one.
    notes: exercise.personalNote ?? null,
    skipped: false,
    cardio: profile.measurementType === "CARDIO_MINUTES"
      ? { minutes: null, completed: false, completedAt: null }
      : null,
    sets: set,
  };
}

export default function ExecutionScreen({ review = false }) {
  const { executionId } = useParams();
  // The execution is read from the TRAIN identity's partition: the
  // authenticated user, or the local recovery user (§9.8). An executionId
  // outside it reads as not found and falls back to /train.
  const identity = useTrainIdentity();
  const userId = identity?.userId;
  const localMode = identity?.mode === "local";
  const navigate = useNavigate();
  // A FINISHED workout opens on Review. The Review's back arrow reopens the
  // workout screen on the same FINISHED record (nothing is un-finished) and
  // says so through this navigation state.
  const editingFinished = useLocation().state?.editFinished === true;
  const { online: browserOnline } = useConnectivity();
  // Server-backed actions (Add Exercise, the picker, Substitute / Restore,
  // Workout Settings, the preference GET and PATCH) need an authenticated
  // identity; local recovery presents them like offline, "Needs a connection".
  const online = browserOnline && canUseServer(identity);
  // Sync chip: a local shell while bootstrap is resolving or unreachable reuses
  // the offline presentation; an anonymous local shell is informational
  // "Sign in to sync" (§10.2); its action is the adjacent SyncAuthNotice.
  const signInRequired = needsSignInToSync(identity);
  // Still authenticated as this partition's user: an AUTH_REQUIRED record then
  // also offers the "Sign in to sync" action (see offersSignInToSync).
  const sessionHeld = canUseServer(identity);
  const chipOnline = localMode ? browserOnline && signInRequired : browserOnline;
  const flushWritesRef = useRef(null);
  const settleWrites = useCallback(() => flushWritesRef.current?.(), []);
  // Ownership comes first: the execution hook writes only while this screen
  // holds the editor lock, and the lock is released only after this screen's
  // committed writes have landed in IndexedDB.
  const ownership = useExecutionOwnership(executionId, { beforeRelease: settleWrites });
  const preferenceState = useWorkoutPreferences(userId, { online });
  // Rest anchors resolve inside the athlete commit path with the preferences
  // known at that moment (§14.5); unavailable preferences use the defaults.
  const execution = useExecution(executionId, userId, {
    writer: ownership.isOwner,
    restPreferences: preferenceState.resolvedPreferences,
  });
  useEffect(() => {
    flushWritesRef.current = execution.flush;
  }, [execution.flush]);
  const sync = useSyncStatus(userId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingExercise, setPendingExercise] = useState(null);
  const [preferencePending, setPreferencePending] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [emptySavePrompt, setEmptySavePrompt] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // §15.8 substitution UX: the slot whose picker is open, and a pending
  // replacement that needs the athlete to confirm discarding entered values.
  const [substituteItemId, setSubstituteItemId] = useState(null);
  const [substituteConfirm, setSubstituteConfirm] = useState(null);
  const finishingRef = useRef(false);
  const activeItemIdRef = useRef(null);

  useEffect(() => {
    activeItemIdRef.current = null;
    finishingRef.current = false;
    setFinishing(false);
  }, [executionId]);

  // Review and the workout screen are one mounted component: reaching Review
  // ends the Finish in flight, so the action is live again if the athlete
  // comes back to the workout screen.
  useEffect(() => {
    if (!review) return;
    finishingRef.current = false;
    setFinishing(false);
  }, [review]);

  const record = execution.record;
  const preferences = preferenceState.preferences || {};
  const preferenceRelevant = Boolean(record && [
    ...(record.document?.items || []).map((item) => effectiveExecutionProfile(record, item)),
    pendingExercise?.executionProfile,
  ].filter(Boolean).some(needsWeightUnit));
  const preferenceKind = !preferenceState.loading && record
    ? requiredPreference(record, preferences, pendingExercise?.executionProfile)
    : null;
  const preferencesUnresolved = preferenceState.loading && preferenceRelevant;
  // Editing waits for the ownership handoff to adopt the latest durable record.
  const disabled = !ownership.isOwner || !execution.writable || saving ||
    preferencesUnresolved || Boolean(preferenceKind);
  const itemLimitReached = nextExecutionItemPosition(record) == null;
  const counts = useMemo(() => executionCounts(record), [record]);
  // An ACTIVE workout reaches Review only with real logged work: a performed
  // set or a completed cardio item. Until then Finish stays in place, locked;
  // Discard is the way out of an empty workout.
  const finishLocked = record?.document?.status === "ACTIVE" && counts.performed === 0;
  // §14.3 rest surface: derived from the persisted restTimer/restOffer and the
  // clock, ACTIVE route only. The current preference mode is deliberately not
  // consulted: an existing timer or offer owns the mode of its anchor (§14.5).
  const rest = useRestTimer(
    !review && record?.document?.status === "ACTIVE" ? record : null,
    { dispatch: run, canEdit: !disabled }
  );
  const restVisible = !review && rest.state !== REST_DISPLAY.HIDDEN;
  // §14.4 Keep Screen Awake: the visible ACTIVE execution only (never Review),
  // whatever the rest state and whether or not this tab owns the editor.
  const previousPerformance = previousPerformanceEnabled(preferenceState.preferences);
  const keepScreenAwake =
    (preferenceState.resolvedPreferences || WORKOUT_PREFERENCE_DEFAULTS).keepScreenAwake === true;
  useScreenWakeLock(!review && record?.document?.status === "ACTIVE" && keepScreenAwake);
  useEffect(() => {
    if (!execution.loading && !record && !execution.storageError?.quarantined) {
      navigate("/train", { replace: true });
    }
  }, [execution.loading, execution.storageError, navigate, record]);

  useEffect(() => {
    if (!record) return;
    if (record.document.status === "DISCARDED") {
      navigate("/train", { replace: true });
    } else if (review && record.document.status === "ACTIVE") {
      navigate(`/train/session/${executionId}`, { replace: true });
    } else if (!review && record.document.status === "FINISHED" && !editingFinished) {
      navigate(`/train/session/${executionId}/review`, { replace: true });
    }
  }, [editingFinished, executionId, navigate, record, review]);

  async function run(action, options) {
    setError("");
    try {
      return await execution.dispatch(action, options);
    } catch (caught) {
      setError(caught?.message || "That change couldn't be saved on this device.");
      return null;
    }
  }

  function activateItem(itemId) {
    // Exercise navigation is ACTIVE activity only; FINISHED edits never touch timing.
    if (disabled || record?.document?.status !== "ACTIVE") return;
    const previousItemId = activeItemIdRef.current;
    if (previousItemId === itemId) return;
    activeItemIdRef.current = itemId;
    if (previousItemId) {
      void run(
        { type: EXECUTION_ACTIONS.NAVIGATE_EXERCISE },
        { flush: true, syncReason: "navigation" }
      );
    }
  }

  // Adds the exercise, then reads its LAST / Suggested inputs in the
  // background (§13.1). The read never blocks or fails the add.
  async function addExerciseItem(exercise) {
    const item = addedItem(exercise, record);
    const added = await run({ type: EXECUTION_ACTIONS.ADD_ITEM, item });
    if (added) void execution.refreshItemHistory?.(item.itemId, { online });
    return added;
  }

  async function answerPreference(value) {
    setPreferencePending(true);
    try {
      await preferenceState.answer({ [preferenceKind]: value });
      if (pendingExercise) {
        const nextPreferences = { ...preferences, [preferenceKind]: value };
        if (!requiredPreference(record, nextPreferences, pendingExercise.executionProfile)) {
          await addExerciseItem(pendingExercise);
          setPendingExercise(null);
          setPickerOpen(false);
        }
      }
    } finally {
      setPreferencePending(false);
    }
  }

  async function chooseExercise(exercise) {
    if (requiredPreference(record, preferences, exercise.executionProfile)) {
      setPendingExercise(exercise);
      return;
    }
    if (itemLimitReached) return;
    await addExerciseItem(exercise);
    setPickerOpen(false);
  }

  // Moves focus to the replacement card's substitution action: the card that
  // opened the picker was replaced by a new item and no longer exists.
  function focusSlot(slotKey) {
    if (!slotKey || typeof document === "undefined") return;
    setTimeout(() => {
      const card = document.querySelector(`[data-slot-key="${slotKey}"]`);
      card?.querySelector(".lz-workout-slot-action")?.focus?.();
    }, 0);
  }

  // One entry point for every substitution, a return to the planned exercise
  // included. The domain decides validity; this only routes its answer: done,
  // needs confirmation, or refused.
  async function applySubstitution(itemId, target, { confirmDiscard = false } = {}) {
    setError("");
    const slotKey = record?.document?.items?.find((item) => item.itemId === itemId)?.prescribedExerciseKey;
    try {
      await execution.substituteItem(itemId, target, { online, confirmDiscard });
      focusSlot(slotKey);
      return "done";
    } catch (caught) {
      if (caught?.code === SUBSTITUTION_ERRORS.CONFIRM_DISCARD_REQUIRED) return "confirm";
      setError(substitutionErrorCopy(caught));
      return "failed";
    }
  }

  async function chooseSubstitute(exercise) {
    const itemId = substituteItemId;
    if (!itemId) return;
    setSubstituteItemId(null);
    const outcome = await applySubstitution(itemId, exercise);
    if (outcome === "confirm") setSubstituteConfirm({ itemId, target: exercise });
  }

  async function confirmSubstitution() {
    const pending = substituteConfirm;
    setSubstituteConfirm(null);
    if (pending) await applySubstitution(pending.itemId, pending.target, { confirmDiscard: true });
  }

  function openWorkoutEditor() {
    navigate(`/train/session/${executionId}`, { replace: true, state: { editFinished: true } });
  }

  async function finish() {
    // Already FINISHED (reopened from Review): nothing to transition, the
    // changes made here are already stored. Back to Review.
    if (record?.document?.status === "FINISHED") {
      navigate(`/train/session/${executionId}/review`, { replace: true });
      return;
    }
    if (finishingRef.current || finishLocked) return;
    finishingRef.current = true;
    setFinishing(true);
    const next = await run({
      type: EXECUTION_ACTIONS.FINISH,
      finishTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }, { flush: true, syncReason: "finish" });
    if (next?.document?.status === "FINISHED") {
      navigate(`/train/session/${executionId}/review`, { replace: true });
      return;
    }
    finishingRef.current = false;
    setFinishing(false);
  }

  // Review's only writes: the workout note (no itemId) and an exercise note.
  function commitNote(itemId, notes) {
    return run({
      type: EXECUTION_ACTIONS.COMMIT_NOTES,
      ...(itemId ? { itemId } : null),
      notes: notes || null,
    });
  }

  async function save() {
    if (counts.performed === 0) {
      setEmptySavePrompt(true);
      return;
    }
    setSaving(true);
    const next = await run({ type: EXECUTION_ACTIONS.SAVE }, { flush: true, syncReason: "save" });
    if (next) {
      if (canUseServer(identity)) await sync.syncNow().catch(() => null);
      await execution.reload();
    }
    setSaving(false);
  }

  async function discard() {
    setSaving(true);
    const next = await run({ type: EXECUTION_ACTIONS.DISCARD }, { flush: true, syncReason: "discard" });
    if (next?.document?.status === "DISCARDED") navigate("/train", { replace: true });
    setSaving(false);
  }

  if (!record && execution.storageError?.quarantined) {
    const conflict = syncConflictCopy(execution.storageError.code);
    return (
      <section className="lz-workout-result">
        <Card>
          <p className="lz-workout-eyebrow">WORKOUT CONFLICT</p>
          <h1>This workout is no longer editable here</h1>
          <p role="status">
            <strong>{conflict.title}.</strong> {conflict.detail}
          </p>
          <Button onClick={() => navigate("/train", { replace: true })}>Back to Train</Button>
        </Card>
      </section>
    );
  }

  if (execution.loading || !record) {
    return <section className="lz-workout-session"><Skeleton shape="block" height={180} /></section>;
  }

  if (record.document.status === "SAVED") {
    return (
      <SavedResult
        record={record}
        signInRequired={signInRequired}
        sessionHeld={sessionHeld}
        onHome={() => navigate("/", { replace: true })}
      />
    );
  }

  const snapshotBlocks = record.server?.snapshot?.blocks || [];
  const represented = new Set(snapshotBlocks.flatMap((block) =>
    record.document.items.filter((item) => item.blockKey === block.blockKey).map((item) => item.itemId)
  ));
  const added = record.document.items.filter((item) => !represented.has(item.itemId));

  const itemProps = {
    record,
    preferences,
    disabled,
    dispatch: run,
    onActivateItem: activateItem,
    online,
    onSubstitute: (item) => setSubstituteItemId(item.itemId),
  };
  const substituteItem = substituteItemId
    ? record.document.items.find((item) => item.itemId === substituteItemId) || null
    : null;
  const confirmItem = substituteConfirm
    ? record.document.items.find((item) => item.itemId === substituteConfirm.itemId) || null
    : null;

  // §15.4: an ACTIVE Free Workout is built as the athlete goes, so Add Exercise
  // is also a body action there (the overflow menu keeps its own entry). Same
  // picker, same online-only / editor / item-limit rule as the menu item.
  // A FINISHED workout reopened from Review keeps the body action it had when
  // Review itself was the editor.
  const freeWorkoutBuilder = !review && (
    record.document.status === "FINISHED" ||
    (record.document.status === "ACTIVE" && record.server?.sourceKind === "FREE")
  );
  const freeAddExercise = (variant, size) => (
    <div className="lz-workout-free-add">
      <Button
        variant={variant}
        size={size}
        disabled={disabled || !online || itemLimitReached}
        onClick={() => setPickerOpen(true)}
      >
        <HeaderIcon name="add" /> Add Exercise
      </Button>
      {!online ? <p className="lz-workout-offline-reason">Add Exercise needs a connection</p> : null}
    </div>
  );

  const items = (
    <div className="lz-workout-items">
      {snapshotBlocks.map((block) => {
        const blockItems = record.document.items.filter((item) => item.blockKey === block.blockKey);
        if (!blockItems.length) return null;
        if (block.grouping === "GROUP") {
          return <SupersetExecutionGroup key={block.blockKey} block={block} items={blockItems} {...itemProps} />;
        }
        return blockItems.map((item) => <ExecutionItemCard key={item.itemId} item={item} {...itemProps} />);
      })}
      {added.map((item) => <ExecutionItemCard key={item.itemId} item={item} {...itemProps} />)}
      {!record.document.items.length ? (
        <Card className="lz-workout-empty">
          <h2>Build this workout as you go</h2>
          <p>Add an exercise to begin logging.</p>
          {freeWorkoutBuilder ? freeAddExercise("primary", "lg") : null}
        </Card>
      ) : null}
      {freeWorkoutBuilder && record.document.items.length ? freeAddExercise("secondary", "sm") : null}
    </div>
  );
  const notices = (
    <>
      {ownership.readOnly ? <Card tone="muted">{EXECUTION_OPEN_ELSEWHERE_MESSAGE}</Card> : null}
      <SyncIssueNotice record={record} />
      <SyncAuthNotice
        userId={userId}
        visible={!ownership.readOnly && offersSignInToSync(record, { signInRequired, sessionHeld })}
      />
      {error || execution.storageError ? (
        <p className="lz-workout-error" role="alert">{error || execution.storageError.message}</p>
      ) : null}
    </>
  );

  return (
    <section
      className={`lz-workout-session${review ? " lz-workout-session--review" : " lz-workout-session--active"}${restVisible ? " lz-workout-session--rest-visible" : ""}`}
      data-review={review || undefined}
    >
      {review ? (
        <>
          <ReviewHeader
            record={record}
            online={chipOnline}
            signInRequired={signInRequired}
            ownership={ownership}
            sync={sync}
            onBack={openWorkoutEditor}
          />
          <ReviewSummary counts={counts} durationSeconds={trackedDurationSeconds(record)} />
          {notices}
          <ReviewReflection
            id="review-workout-note"
            value={record.document.notes || ""}
            maxLength={1000}
            disabled={disabled}
            onCommit={(next) => void commitNote(null, next)}
          />
          <div className="lz-workout-review-section-head">
            <h2>Exercises</h2>
          </div>
          <ReviewExerciseList
            record={record}
            disabled={disabled}
            onCommitNote={(itemId, next) => void commitNote(itemId, next)}
          />
          <StickyBottomActions className="lz-workout-review-actions">
            <Button size="lg" isLoading={saving} loadingLabel="Saving" disabled={disabled} onClick={save}>
              Save Workout <span aria-hidden="true">→</span>
            </Button>
          </StickyBottomActions>
        </>
      ) : (
        <>
          <ActiveExecutionHeader
            record={record}
            online={online}
            chipOnline={chipOnline}
            signInRequired={signInRequired}
            ownership={ownership}
            sync={sync}
            disabled={disabled}
            itemLimitReached={itemLimitReached}
            saving={saving}
            finishing={finishing}
            finishLocked={finishLocked}
            onBack={() => navigate("/", { replace: true })}
            onFinish={finish}
            onOpenSettings={() => {
              const { state, ...location } = createExecutionWorkoutPreferencesLocation(executionId);
              navigate(location, { state });
            }}
            onAddExercise={() => setPickerOpen(true)}
            onDiscard={() => setConfirmDiscard(true)}
            previousPerformance={previousPerformance}
            onTogglePreviousPerformance={() => {
              // A preference write only: never an execution revision or sync.
              void preferenceState.answer({ previousPerformanceDefault: !previousPerformance })
                .catch(() => {});
            }}
          />
          {notices}
          {items}
          <RestTimerBar
            state={rest.state}
            remainingSeconds={rest.remainingSeconds}
            durationSeconds={rest.durationSeconds}
            canEdit={rest.canEdit}
            canExtend={rest.canExtend}
            onPlay={() => void rest.play()}
            onExtend={() => void rest.extend()}
            onDismiss={() => void rest.dismiss()}
          />
        </>
      )}

      {/* Structural editing lives on the workout screen only; Review mounts none of it. */}
      {review ? null : (
        <>
        <ExercisePickerSheet open={pickerOpen} online={online} executionId={executionId} onClose={() => { setPickerOpen(false); setPendingExercise(null); }} onSelect={chooseExercise} />
        <ExercisePickerSheet
          open={Boolean(substituteItem)}
          online={online}
          executionId={executionId}
          onClose={() => setSubstituteItemId(null)}
          onSelect={chooseSubstitute}
          substitute={substituteItem ? {
            record,
            item: substituteItem,
            presentation: substitutionPresentation(record, substituteItem, { online }),
            conflict: prescribedExerciseForItem(record, substituteItem)?.constraintConflict || null,
            showAll: record.pickerShowAll === true,
            onShowAllChange: (value) => void execution.setPickerShowAll(value),
          } : null}
        />
        <Dialog
          open={Boolean(substituteConfirm && confirmItem)}
          onClose={() => setSubstituteConfirm(null)}
          title="Replace this exercise?"
          footer={(
            <div className="lz-workout-dialog-actions">
              <Button variant="ghost" onClick={() => setSubstituteConfirm(null)}>Cancel</Button>
              <Button onClick={() => void confirmSubstitution()}>Replace exercise</Button>
            </div>
          )}
        >
          <p>
            The values you entered for {confirmItem?.exerciseName || "this exercise"} haven’t been
            logged as a set and will be removed.{" "}
            {`${substituteConfirm?.target?.name || "The new exercise"} will take its place in this slot.`}
          </p>
        </Dialog>
        </>
      )}
      <FirstUsePreferenceSheet open={Boolean(preferenceKind)} kind={preferenceKind} pending={preferencePending} onChoose={answerPreference} />

      {review ? null : (
        <Dialog
          open={confirmDiscard}
          onClose={() => setConfirmDiscard(false)}
          title="Discard workout?"
          description="Your logged work will be discarded after it syncs. This can’t be undone."
          footer={(
            <div className="lz-workout-dialog-actions">
              <Button variant="ghost" onClick={() => setConfirmDiscard(false)}>Keep Workout</Button>
              <Button variant="danger" disabled={saving} onClick={discard}>Discard Workout</Button>
            </div>
          )}
        ><span /></Dialog>
      )}

      <Dialog
        open={emptySavePrompt}
        onClose={() => setEmptySavePrompt(false)}
        title="Nothing was logged — discard?"
        footer={(
          <div className="lz-workout-dialog-actions">
            <Button
              variant="ghost"
              onClick={() => {
                setEmptySavePrompt(false);
                openWorkoutEditor();
              }}
            >
              Keep Editing
            </Button>
            <Button variant="danger" onClick={discard}>Discard Workout</Button>
          </div>
        )}
      ><p>A workout needs at least one performed set or completed cardio item to be saved.</p></Dialog>
    </section>
  );
}

export { addedItem, requiredPreference };
